
import React, { useMemo, useState, useEffect } from 'react';
import { GameType } from '../../types';


import { Link } from 'react-router-dom';
import { getProducts, getStores, getActivities, getAllTournaments } from '../../services/supabaseService';
import { useAuth } from '../../components/AuthProvider';
import { useLoginPrompt } from '../../contexts/LoginPromptContext';
import { OfflineWarning } from '../../components/OfflineWarning';
import { fetchLatestPosts, BlogPost } from '../../services/blogService';
import { PromoBanner } from '../../components/PromoBanner';
import { ChangelogWidget } from '../../components/ChangelogWidget';

import { LojistaDashboard } from '../shopkeeper/LojistaDashboard';

interface DashboardProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ activeGame, setActiveGame }) => {
  const { user, login, isOffline } = useAuth();
  const { openLoginPrompt } = useLoginPrompt();
  const isLojista = user?.role_id === 6 || user?.role_id === 1;

  if (isLojista) {
      return <LojistaDashboard />;
  }

  const [products, setProducts] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [dbGames, setDbGames] = useState<any[]>([]);

  useEffect(() => {
    const unsubProducts = getProducts(setProducts);
    const unsubStores = getStores(setStores);
    
    // Fetch real activities
    getActivities(5).then(setActivities);
    
    // Fetch real tournaments
    getAllTournaments().then(setTournaments);
    
    // Fetch cardgames for names
    import('../../services/supabaseService').then(m => m.getCardgames().then(setDbGames));
    
    // Fetch real blog posts from WordPress
    fetchLatestPosts(3).then(posts => {
      setBlogPosts(posts);
      setLoadingBlog(false);
    });

    return () => {
      unsubProducts();
      unsubStores();
    };
  }, []);

  const activeGameName = useMemo(() => {
    if (activeGame === 'All') return 'Todos os Jogos';
    const game = dbGames.find(g => Number(g.id) === Number(activeGame));
    return game ? game.name : 'Jogo Selecionado';
  }, [activeGame, dbGames]);

  const filteredActions = useMemo(() => {
    if (!activities) return [];
    if (activeGame === 'All') return activities;
    // For now, if activeGame is set, we might not have a direct filter on activity_type 
    // but we can try to find relevant strings in target or action if we wanted.
    // Simplifying to just show latest activities for now as requested.
    return activities;
  }, [activeGame, activities]);

  const upcomingTournaments = useMemo(() => {
    if (!tournaments || !Array.isArray(tournaments)) return [];
    return tournaments
      .filter(t => t && (t.status === 'scheduled' || t.status === 'Aberto'))
      .slice(0, 2)
      .map(t => {
        let hostStore = t.store;
        if (!hostStore) {
          const creatorStoreArray = t.creator?.store_members;
          if (Array.isArray(creatorStoreArray) && creatorStoreArray.length > 0) {
            hostStore = creatorStoreArray[0]?.store;
          }
        }
        return {
          id: t.id,
          name: t.name,
          game: t.games?.name || 'TCG',
          date: t.start_date ? new Date(t.start_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : 'TBD',
          location: hostStore ? hostStore.name : 'Local da Loja',
          imageUrl: t.image_url || 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400'
        };
      });
  }, [tournaments]);

  return (
    <div className="space-y-8 md:space-y-10 animate-in fade-in duration-700 pb-10">
      {isOffline && <OfflineWarning />}

      {/* Welcome Banner / Hero */}
      <section className="relative overflow-hidden bg-slate-900/40 border border-slate-800 rounded-[32px] md:rounded-[48px] p-6 md:p-12">
        <div className="relative z-10 max-w-2xl space-y-4 md:space-y-6">
          <div className="inline-flex items-center space-x-2 bg-pink-500/10 border border-pink-500/20 px-3 py-1 rounded-full">
            <i className="fas fa-sparkles text-pink-500 text-[10px]"></i>
            <span className="text-[10px] font-black uppercase tracking-widest text-pink-400">Novo Evento</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight">
            Seja bem-vindo ao <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">Cardumy</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed">
            A central definitiva para gerenciar coleções e dominar o meta de {activeGame === 'All' ? 'qualquer TCG' : activeGameName}.
          </p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
             <Link 
               to="/pastas/colecao" 
               onClick={(e) => {
                 if (!user) {
                   e.preventDefault();
                   openLoginPrompt('Minha Coleção');
                 }
               }}
               className="bg-purple-600 hover:bg-purple-700 text-white font-black px-8 py-3.5 rounded-2xl transition-all shadow-lg shadow-purple-600/20 active:scale-95 text-sm md:text-base flex items-center justify-center gap-2 cursor-pointer"
             >
               {!user && <i className="fas fa-lock text-xs text-slate-300"></i>}
               <span>Minha Coleção</span>
             </Link>
             <Link 
               to="/meus-decks?novo=1" 
               onClick={(e) => {
                 if (!user) {
                   e.preventDefault();
                   openLoginPrompt('Meus Decks');
                 }
               }}
               className="bg-slate-800 hover:bg-slate-700 text-white font-black px-8 py-3.5 rounded-2xl transition-all border border-white/5 active:scale-95 text-sm md:text-base flex items-center justify-center gap-2 cursor-pointer"
             >
               {!user && <i className="fas fa-lock text-xs text-slate-300"></i>}
               <span>Novo Deck</span>
             </Link>
          </div>
        </div>
        
        {/* Subtle Decorative Fish Icon without heavy blur/pulses */}
        <div className="hidden lg:flex absolute right-12 top-1/2 -translate-y-1/2 w-48 h-48 items-center justify-center pointer-events-none">
          <i className="fas fa-fish-fins text-[120px] text-white/5 rotate-[15deg]"></i>
        </div>
      </section>

      

      {/* FERRAMENTAS SOCIAIS DE ELITE (CardLink e Encontrar Pessoas) */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-2">
         {/* Botão CardLink */}
         <Link 
            to="/cardlink" 
            onClick={(e) => {
              if (!user) {
                e.preventDefault();
                openLoginPrompt('CardLink');
              }
            }}
            className="group relative bg-gradient-to-br from-indigo-950/40 to-slate-900 border border-indigo-500/30 p-6 md:p-8 rounded-[32px] overflow-hidden transition-colors hover:border-indigo-400 text-left cursor-pointer"
         >
            <div className="flex items-center space-x-6">
               <div className="w-16 h-16 rounded-2xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 border border-indigo-500/20 relative">
                  <i className="fas fa-link text-2xl"></i>
                  {!user && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-700 text-slate-300 rounded-full flex items-center justify-center text-[9px] font-black shadow ring-2 ring-slate-900" title="Requer Login">
                      <i className="fas fa-lock text-[8px]"></i>
                    </span>
                  )}
               </div>
               <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">CardLink</h3>
                  <p className="text-xs text-slate-400 font-medium max-w-[200px] leading-snug mt-0.5">Encontre quem tem a carta que você deseja na coleção.</p>
               </div>
            </div>
            <div className="absolute bottom-6 right-8 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity">
               <i className="fas fa-arrow-right"></i>
            </div>
         </Link>

         {/* Botão Encontrar Pessoas */}
         <Link 
            to="/comunidade" 
            onClick={(e) => {
              if (!user) {
                e.preventDefault();
                openLoginPrompt('Comunidade');
              }
            }}
            className="group relative bg-gradient-to-br from-pink-950/40 to-slate-900 border border-pink-500/30 p-6 md:p-8 rounded-[32px] overflow-hidden transition-colors hover:border-pink-400 text-left cursor-pointer"
         >
            <div className="flex items-center space-x-6">
               <div className="w-16 h-16 rounded-2xl bg-pink-600/20 flex items-center justify-center text-pink-400 border border-pink-500/20 relative">
                  <i className="fas fa-users-viewfinder text-2xl"></i>
                  {!user && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-700 text-slate-300 rounded-full flex items-center justify-center text-[9px] font-black shadow ring-2 ring-slate-900" title="Requer Login">
                      <i className="fas fa-lock text-[8px]"></i>
                    </span>
                  )}
               </div>
               <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">Encontrar Pessoas</h3>
                  <p className="text-xs text-slate-400 font-medium max-w-[200px] leading-snug mt-0.5">Conecte-se e jogue com membros do cardume na sua região.</p>
               </div>
            </div>
            <div className="absolute bottom-6 right-8 text-pink-500 opacity-0 group-hover:opacity-100 transition-opacity">
               <i className="fas fa-arrow-right"></i>
            </div>
         </Link>
      </section>

      {/* ESPAÇO PUBLICITÁRIO (Banner de Propaganda) */}
      <section className="px-2">
         <PromoBanner />
      </section>

      {/* Últimas postagens */}
      <section className="space-y-6 px-2">
        <div className="flex justify-between items-end">
          <div className="space-y-2">
            <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter">Últimas postagens</h3>
            <div className="h-1 w-12 bg-purple-600 rounded-full"></div>
          </div>
          <a 
            href="https://cardumy.blog/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-slate-800 hover:bg-slate-700 text-white font-black text-[10px] tracking-widest px-6 py-2.5 rounded-xl border border-white/5 transition-all active:scale-95 uppercase"
          >
            Ver no blog
          </a>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {loadingBlog ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-slate-900/40 border border-slate-800 rounded-[32px] overflow-hidden h-[400px] animate-pulse">
                <div className="h-48 bg-slate-800"></div>
                <div className="p-6 space-y-4">
                  <div className="h-4 w-12 bg-slate-800 rounded"></div>
                  <div className="h-6 w-full bg-slate-800 rounded"></div>
                  <div className="h-6 w-2/3 bg-slate-800 rounded"></div>
                </div>
              </div>
            ))
          ) : blogPosts.length > 0 ? (
            blogPosts.map((post) => {
              const featuredImage = post.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=800';
              const categoryName = post.category || 'Post';
              const formattedDate = new Date(post.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

              return (
                <div 
                  key={post.id} 
                  className="group bg-slate-900/60 border border-slate-800 rounded-[32px] overflow-hidden hover:border-purple-500/40 transition-colors flex flex-col h-full shadow-md"
                  id={`blog-post-${post.id}`}
                >
                  <div className="relative h-48 overflow-hidden bg-slate-950">
                    <img 
                      src={featuredImage} 
                      className="w-full h-full object-cover" 
                      alt={post.title} 
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-purple-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg shadow-lg">
                        {categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-4 flex flex-col flex-1">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{formattedDate}</span>
                    <h4 
                      className="text-lg font-black text-white leading-tight group-hover:text-purple-400 transition-colors line-clamp-2"
                    >
                      {post.title}
                    </h4>
                    <div className="pt-2 mt-auto">
                      <span className="text-[10px] font-black text-purple-500 uppercase tracking-widest flex items-center space-x-2">
                        <i className="fas fa-arrow-right text-[8px] group-hover:translate-x-1 transition-transform"></i>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full py-12 text-center text-slate-500 italic border border-dashed border-slate-800 rounded-[32px]">
              Nenhuma postagem encontrada no blog.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
